/** Connection hints are optional: browsers without them start at six and adapt after successes. */
export interface ConnectionHints {
  effectiveType?: string;
  saveData?: boolean;
  downlink?: number;
}

export const MAX_UPLOAD_CONCURRENCY = 10;
const MAX_IN_FLIGHT_BYTES = 64 * 1024 * 1024;

export function uploadPolicy(connection?: ConnectionHints) {
  if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return { initial: 2, max: 2 };
  if (connection?.effectiveType === '3g') return { initial: 3, max: 4 };
  if (connection?.effectiveType === '4g' && (connection.downlink ?? 0) >= 5) return { initial: 10, max: 10 };
  return { initial: 6, max: MAX_UPLOAD_CONCURRENCY };
}

interface Work {
  bytes: number;
  execute: () => Promise<void>;
}

/** One pool serves all batches. New selections cannot multiply the number of active transfers. */
export class UploadScheduler {
  private queue: Work[] = [];
  private active = 0;
  private bytes = 0;
  private limit = 0;
  private successes = 0;

  constructor(private readonly connection: () => ConnectionHints | undefined = () => undefined) {}

  run<T>(bytes: number, task: () => Promise<T>): Promise<T> {
    if (this.active === 0 && this.queue.length === 0) {
      this.limit = uploadPolicy(this.connection()).initial;
      this.successes = 0;
    }
    return new Promise<T>((resolve, reject) => {
      this.queue.push({ bytes, execute: async () => {
        try {
          const result = await task();
          // Add one slot after a healthy wave; failures immediately halve the next wave.
          if (++this.successes >= this.limit) {
            this.limit = Math.min(uploadPolicy(this.connection()).max, this.limit + 1);
            this.successes = 0;
          }
          resolve(result);
        } catch (error) {
          this.limit = Math.max(1, Math.floor(this.limit / 2));
          this.successes = 0;
          reject(error);
        } finally {
          this.active--;
          this.bytes -= bytes;
          this.pump();
        }
      } });
      this.pump();
    });
  }

  private pump() {
    this.limit = Math.min(this.limit, uploadPolicy(this.connection()).max);
    while (this.queue.length && this.active < this.limit) {
      const next = this.queue[0]!;
      // A single large file can always run. Several large originals share a bounded byte budget.
      if (this.active > 0 && this.bytes + next.bytes > MAX_IN_FLIGHT_BYTES) break;
      this.queue.shift();
      this.active++;
      this.bytes += next.bytes;
      void next.execute();
    }
  }
}

export const uploadScheduler = new UploadScheduler(() => typeof navigator === 'undefined'
  ? undefined : (navigator as Navigator & { connection?: ConnectionHints }).connection);
